import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, lastValueFrom } from 'rxjs';

import { environment } from '@environments/environment';
import { EndPointApi } from '@helpers/endpointapi';
import { HEADER_CREDENTIAL_KEY, HEADER_TOKEN_PREFIX } from 'ops/common/auth/AuthConfig';
import { RuntimeEnvironments } from '@environments/runtimeEnviroments.enum';

@Injectable()
export class AuthService {
    activityInterval: any;
    lastActivityTime: number;

    private showSignInSubject = new Subject<boolean>();
    showSignIn$ = this.showSignInSubject.asObservable();
    private showSignOutSubject = new Subject<boolean>();
    showSignOut$ = this.showSignOutSubject.asObservable();
    private isLoggedinSubject = new BehaviorSubject<boolean>(false);
    isLoggedin$ = this.isLoggedinSubject.asObservable();
    private userListUpdatedSubject = new Subject<boolean>();
    userListUpdated$ = this.userListUpdatedSubject.asObservable();
    private showNotificationSubject = new Subject<boolean>();
    showNotification$ = this.showNotificationSubject.asObservable();
    private ACTIVITY_TIME = 10 * 60 * 1000;

    private currentUserSubject = new BehaviorSubject<ICurrentUser>({
        token: '',
        userRoleType: null,
        userId: null,
    });
    currentUser$ = this.currentUserSubject.asObservable();

    private domain: string;
    private headers: any = new HttpHeaders({
        'Content-Type': 'application/json',
    });

    constructor(private http: HttpClient) {
        this.domain = EndPointApi.iApiDomain();
        if (this.isLoggedinSubject.value) {
            this.startActivityTracking();
        }
        this.updateActivity = this.updateActivity.bind(this);
    }

    async getUserRoles(): Promise<{ id: string; role: string }[]> {
        return lastValueFrom(
            this.http.get<{ id: string; role: string }[]>(`${this.domain}/api/ops/roles`),
        );
    }

    async signIn(roleId: string, password: string): Promise<any> {
        const params = {
            roleId,
            password,
        };

        return lastValueFrom(
            this.http.post(
                `${this.domain}/api/ops/signin`,
                {
                    params: params,
                    headers: this.headers,
                },
                { observe: 'response', withCredentials: true },
            ),
        )
            .then(
                (
                    response: HttpResponse<{
                        role: string;
                        id: string;
                    }>,
                ) => {
                    const access = response.headers.get(HEADER_CREDENTIAL_KEY);
                    const userRoleType = response.body.role;
                    const userId = response.body.id;
                    this.isLoggedinSubject.next(true);
                    this.saveCurrentUser(access, userRoleType, userId);
                    this.startActivityTracking();
                },
            )
            .catch((error) => {
                throw error;
            });
    }

    getCurrentUserToken(): string {
        return this.currentUserSubject.value.token;
    }

    getCurrentUserRoleType(): string {
        return this.currentUserSubject.value.userRoleType;
    }

    getCurrentUserId(): string {
        return this.currentUserSubject.value.userId;
    }

    saveCurrentUser(accessToken?: string, userRoleType?: string, userId?: string) {
        if (!accessToken || !userRoleType || !userId) {
            this.currentUserSubject.next(this.currentUserSubject.value);
            return;
        }
        this.currentUserSubject.next({
            token: accessToken.replace(HEADER_TOKEN_PREFIX, ''),
            userRoleType: userRoleType,
            userId: userId,
        });
    }

    startActivityTracking() {
        this.activityInterval = setInterval(() => this.sendActivityStatus(), this.ACTIVITY_TIME);
        window.addEventListener('mousemove', this.updateActivity);
        window.addEventListener('keydown', this.updateActivity);
    }

    stopActivityTracking() {
        window.removeEventListener('mousemove', this.updateActivity);
        window.removeEventListener('keydown', this.updateActivity);
        clearInterval(this.activityInterval);
    }

    sendActivityStatus() {
        const inactiveTime = Date.now() - this.lastActivityTime;
        if (inactiveTime >= this.ACTIVITY_TIME) {
            this.showSignOutOverlay();
        }
    }

    showSignOutOverlay() {
        this.signOut();
        this.showSignOutSubject.next(true);
    }

    signOut() {
        this.currentUserSubject.next({
            token: '',
            userRoleType: null,
            userId: null,
        });
        this.isLoggedinSubject.next(false);
        this.stopActivityTracking();
    }

    updateActivity() {
        this.lastActivityTime = Date.now();
        clearInterval(this.activityInterval);
        this.activityInterval = setInterval(() => this.sendActivityStatus(), this.ACTIVITY_TIME);
    }

    showSignInOverlay() {
        this.showSignInSubject.next(true);
    }

    getEnvironment(): RuntimeEnvironments {
        return environment.env;
    }

    getDomain(): string {
        return this.domain;
    }

    updateUserList() {
        this.userListUpdatedSubject.next(true);
    }

    showNotificationOverlay() {
        this.showNotificationSubject.next(true);
    }

    async checkChannelSettingPermission(userId, groupId) {
        const response: any = await lastValueFrom(
            this.http.post(`${this.domain}/api/ops/channelSetting/checkPermission`, {
                userId,
                groupId,
            }),
        );
        return response.hasPermission;
    }
}

export interface ICurrentUser {
    token: string;
    userRoleType: string;
    userId: string;
}
